import {ReactNode} from 'react';

import {makeEnumTypeGuard} from '../../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../models/api/analyticsMetricsModel';
import {
  AnalyticsReportFilterOptions,
  AnalyticsSavedViewFiltersModel,
} from '../../../models/api/analyticsSavedViewModel';

export interface ReportMetricConfig<T extends AnalyticsMetricsKeys> {
  id: T;
  isDisplayedByDefault: boolean;
  isDisabled?: boolean;
  category?: MetricCategoriesEnum;
}

export interface ReportConfigElement<T extends AnalyticsMetricsKeys = AnalyticsMetricsKeys> {
  id: ReportElementIdsEnum;
  // All metrics available on that report element. The order will be used in metric picker dropdowns.
  metrics: ReadonlyArray<ReportMetricConfig<T>>;
  // For table metrics, they need to define which columns should be displayed.
  columns?: ReadonlyArray<ReportMetricConfig<T>>;
}

export enum ConversationResolutionStrategiesEnum {
  TICKETING = 'Ticketing',
  NONE = 'None',
}

export enum AnalyticsFeatureStatusesEnum {
  NEW = 'new',
  BETA = 'beta',
  LEGACY = 'legacy',
}

export interface AnalyticsReportConfig {
  title: ReactNode;
  reportElements: ReadonlyArray<ReportConfigElement>;
  options?: AnalyticsReportFilterOptions;

  featureStatus?: AnalyticsFeatureStatusesEnum;
}

export interface AnalyticsReportQueryParams {
  metric?: AnalyticsMetricsKeys;
  metrics?: ReadonlyArray<AnalyticsMetricsKeys>;
  period: {
    start?: string;
    end?: string;
    tz: string;
  };
  filters: AnalyticsSavedViewFiltersModel;
}

export enum ReportFilterOptionsEnum {
  /*
   * The `customers_table` metric, by default, returns a mix of card IDs and contact IDs. This option forces it to
   * always return contact IDs.
   */
  ALWAYS_EMIT_CONTACT_ID = 'always_emit_contact_id',
  BUCKET_BOUNDARIES = 'bucket_boundaries', // Set custom buckets for donut chart.
  COLLECT_CONTRIBUTING_SEGMENT_IDS = 'collect_contributing_segment_ids',
  SELECTED_METRICS = 'selected_metrics',
  TAG_MODE = 'tag_mode', // Is the tag still applied or was it applied during period?
  /*
   * By explicitly specifying which metrics we need to have calculated for the previous period we can avoid doing so
   * unnecessarily.
   */
  PREVIOUS_PERIOD_METRICS = 'previous_period_metrics',

  /*
   * Indicate which metrics have the previous period displayed,
   * and for which the previous period is not displayed by default
   */
  SELECTED_PREVIOUS_PERIODS = 'selected_previous_periods',
  /**
   * For the `group_by_inbox/tag` metrics, specify which metric should be "grouped by".
   */
  GROUP_BY_SUB_METRICS = 'group_by_sub_metrics',
}

export enum ContributingSegmentsOptionsEnum {
  ROW = 'row',
  SUB_METRIC = 'sub_metric',
}

export interface BasicReportProps {
  isLoading: boolean;
}

export enum AnalyticsReportsEnum {
  // Analytics V3 Reports.
  WORKLOAD = 'workload',
  RESOLUTIONS = 'resolutions',
  // Analytics legacy reports.
  OVERVIEW = 'overview',
  TEAM_PERF = 'team_performance',
  CONVERSATIONS = 'conversations',
  TAGS = 'tags',
  MESSAGE_TEMPLATES = 'message_templates',
  SLA = 'sla',
  CUSTOMER_EXPERIENCE = 'customer_experience',
  // Automation reports.
  CHATBOTS = 'chatbots',
  KNOWLEDGE_BASE = 'knowledge_base',
  SUPPORT_REPORT = 'support_report',
  // Real-time reports.
  LIVE_DASHBOARD = 'live_dashboard',
  WEEKLY_REPORT = 'weekly_report',
}

export const AnalyticsV3Reports = [AnalyticsReportsEnum.WORKLOAD, AnalyticsReportsEnum.RESOLUTIONS];

export const analyticsReportsWithUrlFilters = [
  AnalyticsReportsEnum.WORKLOAD,
  AnalyticsReportsEnum.RESOLUTIONS,
  AnalyticsReportsEnum.OVERVIEW,
  AnalyticsReportsEnum.TEAM_PERF,
  AnalyticsReportsEnum.CONVERSATIONS,
  AnalyticsReportsEnum.TAGS,
  AnalyticsReportsEnum.MESSAGE_TEMPLATES,
  AnalyticsReportsEnum.SLA,
  AnalyticsReportsEnum.CUSTOMER_EXPERIENCE,
];

export const analyticsReportsWithConfig = [
  AnalyticsReportsEnum.WORKLOAD,
  AnalyticsReportsEnum.RESOLUTIONS,
  AnalyticsReportsEnum.OVERVIEW,
  AnalyticsReportsEnum.TEAM_PERF,
  AnalyticsReportsEnum.CONVERSATIONS,
  AnalyticsReportsEnum.TAGS,
  AnalyticsReportsEnum.MESSAGE_TEMPLATES,
  AnalyticsReportsEnum.SLA,
  AnalyticsReportsEnum.CUSTOMER_EXPERIENCE,
  AnalyticsReportsEnum.KNOWLEDGE_BASE,
];

export type AnalyticsReportsWithConfig =
  | AnalyticsReportsEnum.WORKLOAD
  | AnalyticsReportsEnum.RESOLUTIONS
  | AnalyticsReportsEnum.OVERVIEW
  | AnalyticsReportsEnum.TEAM_PERF
  | AnalyticsReportsEnum.CONVERSATIONS
  | AnalyticsReportsEnum.TAGS
  | AnalyticsReportsEnum.MESSAGE_TEMPLATES
  | AnalyticsReportsEnum.SLA
  | AnalyticsReportsEnum.CUSTOMER_EXPERIENCE
  | AnalyticsReportsEnum.KNOWLEDGE_BASE;

export function isAnalyticsReportWithConfig(
  report: AnalyticsReportsEnum,
): report is AnalyticsReportsWithConfig {
  return analyticsReportsWithConfig.includes(report);
}

export enum ReportElementIdsEnum {
  // WORKLOAD
  WORKLOAD_KEY_METRICS = 'workload-key-metrics',
  WORKLOAD_OVER_TIME = 'workload-over-time',
  WORKLOAD_BUSIEST_TIMES = 'workload-busiest-times',
  WORKLOAD_EFFICIENCY = 'workload-efficiency',
  WORKLOAD_CONVERSATION_STAGES = 'workload-conversation-stages',
  WORKLOAD_BY_INBOX = 'workload-by-inbox',
  WORKLOAD_STAGE_BANNER = 'workload-stage-banner',
  // RESOLUTIONS
  RESOLUTIONS_KEY_METRICS = 'resolutions-key-metrics',
  // OVERVIEW
  OVERVIEW_METRICS_CARDS = 'overview-metrics-cards',
  OVERVIEW_RESOLVED_PCT = 'resolved-segments-pct',
  OVERVIEW_STAGE_RESOLVED_PCT = 'stage-resolved-segments-pct',
  OVERVIEW_CHANGES_OVER_TIME = 'overview-changes-over-time',
  OVERVIEW_REPLY_TIMES_WRAPPER = 'reply-times-teammates-and-tags',
  OVERVIEW_REPLY_TIMES_DONUT = 'reply-times-donut',
  OVERVIEW_REPLY_TIMES_HISTOGRAM = 'reply-time-histogram',
  OVERVIEW_TEAM_CHANGES_HISTOGRAM = 'team-changes-list',
  OVERVIEW_TAGS_HISTOGRAM = 'top-tags-list',
  // TEAM PERF
  TEAM_METRICS_CARDS = 'team-metrics-cards',
  TEAM_HEATMAP = 'busiest-times',
  TEAM_TEAMMATES_TABLE = 'teammates-table',
  // CONVERSATIONS
  CONVERSATIONS_WHAT_HAPPENED = 'what-happened',
  CONVERSATIONS_SANKEY = 'conversation-flows',
  CONVERSATIONS_INBOXES = 'inboxes',
  // TAGS
  TAGS_CONVERSATIONS_PCT = 'conversation-with-tags',
  TAGS_PERFORMANCE_TABLE = 'individual-tag-performance',
  TAG_CHANGES_TABLE = 'tag-changes',
  // MESSAGE TEMPLATES
  MESSAGE_TEMPLATES_PCT = 'messages-with-templates',
  MESSAGE_TEMPLATE_TABLE = 'message-template-table',
  // SLA
  SLA_METRICS_CARDS = 'sla-metrics-cards',
  SLA_PERF_GRAPH = 'sla-performance-graph',
  SLA_PERF_GRAPH_V2 = 'sla-performance-graph-v2',
  SLA_STATUS_TABLE_V2 = 'sla-status-table-v2',
  // CUSTOMER EXPERIENCE
  CX_TOTAL_SCALAR = 'customer-experience-total-scalar',
  CX_SCORES_DONUT_WRAPPER = 'customer-experience-scores-donut',
  CX_SCORES_LIST_WRAPPER = 'customer-experience-scores-list',
  CX_SCORES_AVG = 'customer-experience-scores-average',
  CX_CHANGES_OVER_TIME_CHART = 'customer-experience-changes-over-time',
  CX_SAMPLE_FEEDBACK_TABLE = 'customer-experience-report-sample-feedback-table',
  CX_TABLE = 'customer-experience-report-table',
  CX_CUSTOMERS_TABLE_V2 = 'customer-experience-report-customers-table-v2',
  CX_ACCOUNTS_TABLE = 'customer-experience-report-accounts-table',
  // CHATBOT/AI ANALYTICS
  CHATBOT_BASE_ANALYTICS = 'automation-reports-chatbot-base-analytics',
  CHATBOT_AI_ANALYTICS = 'automation-reports-chatbot-ai-analytics',
  // KNOWLEDGE BASE
  KNOWLEDGE_BASE_METRIC_CARDS = 'knowledge-base-metric-cards',
  KNOWLEDGE_BASE_CHANGES_OVER_TIME = 'knowledge-base-changes-over-time',
  KNOWLEDGE_BASE_ENGAGEMENT_TABLE = 'knowledge-base-engagement-table',
  KNOWLEDGE_BASE_SEARCH_TERMS_TABLE = 'knowledge-base-search-terms-table',
  // LIVE DASHBOARD
  LIVE_DASHBOARD_OPEN_COUNT = 'live-dashboard-open-count',
  LIVE_DASHBOARD_UNASSIGNED_COUNT = 'live-dashboard-unassigned-count',
  LIVE_DASHBOARD_WAITING_COUNT = 'live-dashboard-waiting-count',
  LIVE_DASHBOARD_OLDEST_OPEN_DELAY = 'live-dashboard-oldest-open-delay',
  LIVE_DASHBOARD_OLDEST_OPEN_CONVERSATIONS_TABLE = 'live-dashboard-oldest-open-conversations-table',
  LIVE_DASHBOARD_ASSIGNED_BY_TEAMMATE = 'live-dashboard-assigned-by-teammate',
  LIVE_DASHBOARD_WORKLOAD_BY_INBOX_TABLE = 'live-dashboard-workload-by-inbox-table',
}

export enum MetricCategoriesEnum {
  VOLUME = 'volume',
  EFFICIENCY = 'efficiency',
  WORKFORCE = 'workforce',
}

export const isAnalyticsReportEnum = makeEnumTypeGuard(AnalyticsReportsEnum);

/*
 * Conversation stages metrics.
 */

// Non-histogram metrics are included so that they are fetched for donut sections.
// The list of metrics in the dropdown is defined in the report itself.
export const histogramConversationStagesMetrics = [
  {id: AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME, isDisplayedByDefault: false},
  {id: AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION, isDisplayedByDefault: false},
];

export const histogramTicketingMetrics = [
  {id: AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME, isDisplayedByDefault: false},
  {id: AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION, isDisplayedByDefault: false},
];

export const scalarConversationStagesMetrics = [
  {id: AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION, isDisplayedByDefault: false},
];

export const scalarTicketingMetrics = [
  {id: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION, isDisplayedByDefault: false},
];

export const graphConversationStagesMetrics = [
  {id: AnalyticsGraphMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH, isDisplayedByDefault: false},
];

export const graphTicketingMetrics = [
  {id: AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH, isDisplayedByDefault: false},
  {id: AnalyticsGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH, isDisplayedByDefault: false},
];

export const heatmapConversationStagesMetrics = [
  {id: AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS, isDisplayedByDefault: false},
];

export const heatmapTicketingMetrics = [
  {id: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS, isDisplayedByDefault: false},
  {id: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS, isDisplayedByDefault: false},
];

export const analyticsConversationStagesMetrics: ReadonlyArray<AnalyticsMetricsKeys> = Array.from(
  new Set([
    ...histogramConversationStagesMetrics.map((metric) => metric.id),
    ...scalarConversationStagesMetrics.map((metric) => metric.id),
    ...graphConversationStagesMetrics.map((metric) => metric.id),
    ...heatmapConversationStagesMetrics.map((metric) => metric.id),
    AnalyticsTableMetricsEnum.STAGE_PCT_RESOLVED_SEGMENTS_TABLE,
  ]),
);

export const analyticsTicketingMetrics: ReadonlyArray<AnalyticsMetricsKeys> = Array.from(
  new Set([
    ...histogramTicketingMetrics.map((metric) => metric.id),
    ...scalarTicketingMetrics.map((metric) => metric.id),
    ...graphTicketingMetrics.map((metric) => metric.id),
    ...heatmapTicketingMetrics.map((metric) => metric.id),
  ]),
);
